import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MockExam from '../components/MockExam';
import MockExamResults from '../components/MockExamResults';
import { hasSavedProgress, getProgressSummary, clearExamProgress } from '../utils/examStorage';

const MockExamPage = ({ examVersion = 'v1' }) => {
  const [examState, setExamState] = useState('intro'); // 'intro', 'taking', 'results'
  const [examData, setExamData] = useState(null);
  const [savedProgress, setSavedProgress] = useState(null);
  const [resumeMode, setResumeMode] = useState(false);

  // Check for saved progress on component mount
  useEffect(() => {
    if (hasSavedProgress(examVersion)) {
      const progressSummary = getProgressSummary(examVersion);
      setSavedProgress(progressSummary);
    }
  }, [examVersion]);

  const handleStartNewExam = () => {
    // Clear any existing progress and start fresh
    clearExamProgress(examVersion);
    setResumeMode(false);
    setSavedProgress(null);
    setExamState('taking');
  };

  const handleResumeExam = () => {
    setResumeMode(true);
    setExamState('taking');
  };

  const handleStartExam = () => {
    setExamState('taking');
  };

  const handleExamComplete = (questions, selectedAnswers) => {
    setExamData({ questions, selectedAnswers });
    setExamState('results');
  };

  const handleRetakeExam = () => {
    clearExamProgress(examVersion);
    setExamData(null);
    setResumeMode(false);
    setSavedProgress(null);
    setExamState('intro');
  };

  const handleBackToHome = () => {
    const confirmed = window.confirm(
      'Are you sure you want to leave the exam?\n\n' +
      'Your progress has been automatically saved and you can resume later.'
    );
    
    if (confirmed) {
      // Navigate back to home - we'll use Link navigation
      return true;
    }
    return false;
  };

  if (examState === 'taking') {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link 
                to="/" 
                className="text-blue-600 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  if (handleBackToHome()) {
                    window.location.href = '/';
                  }
                }}
              >
                ← Back to Home
              </Link>
              
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    const confirmed = window.confirm(
                      'Are you sure you want to reset your progress?\n\n' +
                      'This will clear all your answers and start a new exam.'
                    );
                    if (confirmed) {
                      handleRetakeExam();
                    }
                  }}
                  className="px-4 py-2 bg-orange-200 text-orange-800 hover:bg-orange-300 rounded-md transition-colors"
                >
                  🔄 Reset Progress
                </button>
              </div>
            </div>
          </div>
        </nav>

        <main className="container mx-auto px-4 py-8">
          <MockExam examVersion={examVersion} onExamComplete={handleExamComplete} shouldResumeProgress={resumeMode} />
        </main>
      </div>
    );
  }

  if (examState === 'results' && examData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="text-blue-600 hover:underline">
                ← Back to Home
              </Link>
              
              <div className="flex space-x-4">
                <button
                  onClick={handleRetakeExam}
                  className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors"
                >
                  🔄 Take New Exam
                </button>
              </div>
            </div>
          </div>
        </nav>

        <main className="container mx-auto px-4 py-8">
          <MockExamResults
            questions={examData.questions}
            selectedAnswers={examData.selectedAnswers}
            onRetakeExam={handleRetakeExam}
          />
        </main>
      </div>
    );
  }

  // Intro state
  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-blue-600 hover:underline">
              ← Back to Home
            </Link>
            
            <div className="flex space-x-4">
              {/* Navigation items can be added here if needed */}
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Mock RAE Exam{examVersion === 'v2' ? ' V2' : ''}
            </h1>
            <p className="text-xl text-gray-600">
              Practice for the South African Radio Amateur Examination
            </p>
          </div>

        {/* Exam Information */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            About the Mock Exam
          </h2>
          
          <div className="prose max-w-none text-gray-700 mb-8">
            <p>
              This mock exam simulates the official South African Radio Amateur Examination (RAE) 
              administered by SARL. It follows the exact format and scoring requirements of the real exam.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Exam Format */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                📋 Exam Format
              </h3>
              <ul className="space-y-2 text-blue-800 text-sm">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <div>
                    <strong>100 questions total</strong> - Multiple choice format
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <div>
                    <strong>Questions 1-40:</strong> Regulations and Operating Practices
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <div>
                    <strong>Questions 41-100:</strong> Technical (Theory)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <div>
                    <strong>No time limit</strong> - Take your time to think through each question
                  </div>
                </li>
              </ul>
            </div>

            {/* Passing Requirements */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-green-900 mb-4">
                ✅ Passing Requirements
              </h3>
              <ul className="space-y-2 text-green-800 text-sm">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <div>
                    <strong>Regulations section:</strong> Minimum 50% (20/40 questions)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <div>
                    <strong>Technical section:</strong> Minimum 50% (30/60 questions)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <div>
                    <strong>Overall average:</strong> Minimum 65% (65/100 questions)
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">•</span>
                  <div>
                    <strong>All three criteria</strong> must be met to pass
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* What to Expect */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-900 mb-4">
              💡 What to Expect
            </h3>
            <ul className="space-y-2 text-yellow-800 text-sm">
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <div>
                  {examVersion === 'v2'
                    ? 'Questions are based on the SARL Class A manual (2024) and the South African regulations in SARL’s 2016 booklet. Answer from memory; no reference materials are provided during the exam.'
                    : 'Questions are randomly selected from all chapters and regulations'}
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <div>
                  You will not see correct answers until you complete the entire exam
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <div>
                  You can navigate between questions and change your answers before finishing
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-600 mr-2">•</span>
                <div>
                  After completion, you'll receive detailed results and study recommendations
                </div>
              </li>
            </ul>
          </div>

          {/* Study Tips */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              📚 Study Tips
            </h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <div>
                  Review all chapters and regulations before taking the mock exam
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <div>
                  Take multiple mock exams to identify your weak areas
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <div>
                  Focus extra study time on topics where you scored poorly
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <div>
                  Aim to consistently score above 70% before taking the real exam
                </div>
              </li>
            </ul>
          </div>

          {/* Resume Progress or Start New */}
          {savedProgress ? (
            <div className="text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold text-blue-900 mb-4">
                  📚 Saved Progress Found
                </h3>
                <div className="text-blue-800 space-y-2 mb-4">
                  <p><strong>Last saved:</strong> {savedProgress.timestamp}</p>
                  <p><strong>Current question:</strong> {savedProgress.currentQuestion} of {savedProgress.totalQuestions}</p>
                  <p><strong>Questions answered:</strong> {savedProgress.answeredCount}/{savedProgress.totalQuestions}</p>
                  <p><strong>Current section:</strong> {savedProgress.currentSection}</p>
                  <div className="bg-blue-100 rounded-full h-3 mt-2">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${savedProgress.progressPercentage}%` }}
                    />
                  </div>
                  <p className="text-sm">{savedProgress.progressPercentage}% complete</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={handleResumeExam}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-green-700 transition-colors shadow-lg"
                  >
                    📖 Resume Exam
                  </button>
                  <button
                    onClick={handleStartNewExam}
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    🔄 Start New Exam
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-3">
                  Starting a new exam will permanently delete your saved progress
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Ready to test your knowledge?
              </h3>
              <p className="text-gray-600 mb-6">
                Make sure you have studied the material and are prepared for a full exam experience.
              </p>
              
              <button
                onClick={handleStartNewExam}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
              >
                Start Mock Exam{examVersion === 'v2' ? ' V2' : ''}
              </button>
              
              <div className="mt-4 text-sm text-gray-500">
                This exam contains 100 questions and may take 1-2 hours to complete
              </div>
              <div className="mt-2 text-xs text-green-600">
                ✓ Your progress will be automatically saved
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Need to review first?
          </h3>
          <div className="flex justify-center space-x-4">
            <Link
              to="/"
              className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Go Back
            </Link>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
};

export default MockExamPage;
